import type { NormalizedDevice, StoredPreference } from './types';

const PREFERENCE_KEY = 'audioDevicePreference';
const BT_DISMISSED_KEY = 'audioDeviceBtDismissed';

/** Minimum confidence score to consider a match valid */
const MATCH_THRESHOLD = 40;

interface ScoredMatch {
  device: NormalizedDevice;
  score: number;
}

/**
 * Persists and recovers device preferences using a rich metadata bundle
 * instead of a raw deviceId. Handles Safari's rotated deviceId values
 * via scored fuzzy matching.
 */
export class DevicePreferenceStore {
  /** Save a device preference to localStorage */
  save(device: NormalizedDevice, userId?: string): void {
    const preference: StoredPreference = {
      browserDeviceId: device.browserDeviceId,
      groupId: device.groupId,
      rawLabel: device.rawLabel,
      displayLabel: device.displayLabel,
      internalId: device.internalId,
      transport: device.transport,
      timestamp: Date.now(),
      userId,
    };

    try {
      localStorage.setItem(PREFERENCE_KEY, JSON.stringify(preference));
    } catch {
      // localStorage quota exceeded or unavailable — silently fail
    }
  }

  /** Load the stored preference from localStorage */
  load(userId?: string): StoredPreference | null {
    try {
      const raw = localStorage.getItem(PREFERENCE_KEY);
      if (!raw) return null;

      const preference = JSON.parse(raw) as StoredPreference;

      // If userId is provided and doesn't match, treat as no preference
      if (userId && preference.userId && preference.userId !== userId) {
        return null;
      }

      return preference;
    } catch {
      return null;
    }
  }

  /** Clear the stored preference */
  clear(): void {
    try {
      localStorage.removeItem(PREFERENCE_KEY);
    } catch {
      // silently fail
    }
  }

  /**
   * Find the best matching device from the current list using scored matching.
   * Returns null if no match exceeds the confidence threshold.
   */
  findBestMatch(
    preference: StoredPreference,
    devices: NormalizedDevice[],
  ): NormalizedDevice | null {
    if (devices.length === 0) return null;

    const scored: ScoredMatch[] = devices.map(device => ({
      device,
      score: this.scoreMatch(preference, device),
    }));

    // Sort by score descending
    scored.sort((a, b) => b.score - a.score);

    const best = scored[0];
    if (best.score >= MATCH_THRESHOLD) {
      return best.device;
    }

    return null;
  }

  /**
   * Score how well a device matches a stored preference.
   * Higher is better. Returns 0-110.
   */
  scoreMatch(preference: StoredPreference, device: NormalizedDevice): number {
    let score = 0;

    // 1. Exact browserDeviceId match (strongest, but rotates on Safari)
    if (preference.browserDeviceId && device.browserDeviceId === preference.browserDeviceId) {
      score = 100;
    }
    // 2. internalId match (stable hash, second-best)
    else if (preference.internalId && device.internalId === preference.internalId) {
      score = 90;
    }
    // 3. groupId + rawLabel match
    else if (
      preference.groupId &&
      preference.rawLabel &&
      device.groupId === preference.groupId &&
      device.rawLabel === preference.rawLabel
    ) {
      score = 70;
    }
    // 4. Fuzzy label similarity
    else if (preference.rawLabel && preference.displayLabel) {
      if (device.rawLabel.includes(preference.rawLabel) || preference.rawLabel.includes(device.rawLabel)) {
        score = 45;
      } else if (
        device.displayLabel.includes(preference.displayLabel) ||
        preference.displayLabel.includes(device.displayLabel)
      ) {
        score = 40;
      }
    }

    // 5. Transport preference weighting — bonus for builtin/usb
    if (score > 0 && (device.transport === 'builtin' || device.transport === 'usb')) {
      score += 10;
    }

    return score;
  }

  /** Suppress the bluetooth quality warning for a specific device */
  suppressBluetoothWarning(deviceInternalId: string): void {
    try {
      const dismissed = this.getBluetoothDismissed();
      if (!dismissed.includes(deviceInternalId)) {
        dismissed.push(deviceInternalId);
        localStorage.setItem(BT_DISMISSED_KEY, JSON.stringify(dismissed));
      }
    } catch {
      // silently fail
    }
  }

  /** Check if the bluetooth warning has been suppressed for a device */
  isBluetoothWarningSuppressed(deviceInternalId: string): boolean {
    return this.getBluetoothDismissed().includes(deviceInternalId);
  }

  private getBluetoothDismissed(): string[] {
    try {
      const raw = localStorage.getItem(BT_DISMISSED_KEY);
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  }
}
